import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { AuthService } from '../../../auth/auth.service';
import { ChatContext } from '../../../coaching-base/chat-context.model';
import {
    CoachingEventStatus,
    CommitmentChat,
} from '../../../coaching-base/general';
import { ChatDialogData } from '../../../coaching/detail/teamschat-dialog/model/chat-dialog-data.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { useDispatcher, UseStore } from '../../../store.decorator';
import { ChatMenuDialogService } from '../dialog';
import { ChatNav } from '../dialog/model/chat-nav.model';
import { navigate } from '../dialog/state/navigate.action';

import { ChatSummary } from './model/chat-summary.model';
import { filterSummary } from './state/filter-summary.action';
import { load } from './state/load.action';
import { toggleCategory } from './state/toggle-category.action';
import { TranslationKeyMap } from './teamschat-summary.i18n';
import { teamschatSummaryReducer } from './teamschat-summary.reducer';
import { TeamschatSummaryState } from './teamschat-summary.state';

@Component({
    selector: 'cmps-teamschat-summary',
    templateUrl: './teamschat-summary.component.html',
    host: {
        class: 'cmps-teamschat-summary',
    },
})
@TakeUntilDestroy()
@UseStore('coachingDetail.TeamschatSummary', teamschatSummaryReducer)
export class TeamschatSummaryComponent implements OnInit, OnDestroy {
    state: TeamschatSummaryState;

    /** @private */
    _i18n = TranslationKeyMap;
    private _keywordSubj: Subject<string>;
    private _keywordSub: Subscription;

    constructor(
        private readonly _authService: AuthService,
        private readonly _dialogService: ChatMenuDialogService,
    ) {}

    async ngOnInit(): Promise<void> {
        this._keywordSubj = new Subject();
        this._keywordSub = this._keywordSubj
            .asObservable()
            .pipe(
                debounceTime(500),
                tap((keyword) => {
                    useDispatcher(this, filterSummary({ keyword }));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useDispatcher(
            this,
            load({
                userId: this._authService.user.id,
            }),
        );
    }

    ngOnDestroy(): void {
        this._keywordSub?.unsubscribe();
        this._keywordSubj?.complete();
    }

    /** @private */
    _onCategoryOpenChange(category: ChatSummary, isOpen: boolean): void {
        useDispatcher(this, toggleCategory({ category, isOpen }));
    }

    /** @private */
    _onKeywordFilterChange(value: string): void {
        this._keywordSubj.next(value);
    }

    /** @private */
    _onChatClick(hasMulti: boolean, sub: CommitmentChat): void {
        if (hasMulti) {
            return;
        }

        let chatContext: ChatContext | null;
        try {
            chatContext = JSON.parse(sub.chat.context);
        } catch {}

        const ctx: ChatContext = {
            commitmentId: sub.commitmentId,
            behaviourId: sub.behaviourId,
            subBehaviourId: sub.subBehaviourId,
            coachId: chatContext?.coachId ?? 0,
            performerId: chatContext?.performerId ?? 0,
        };

        const data: ChatDialogData = {
            chatRequest: {
                chatId: sub.chat.chatId,
                members: [
                    ctx.coachId,
                    ctx.performerId,
                ] /* TODO revise. use sub.chat.members??? */,
                context: JSON.stringify(ctx),
            },
            canCreateNotes: [
                CoachingEventStatus.DRAFT,
                CoachingEventStatus.ACTIVE,
            ].includes(
                sub.draftExists === true
                    ? sub.draftStatusId
                    : sub.commitmentStatusId,
            ),
        };
        useDispatcher(
            this._dialogService,
            navigate({
                nav: ChatNav.CHAT_BOX,
                ctx: {
                    chatBoxData: data,
                },
            }),
        );
    }
}
